import styled from "styled-components";
import { InpatientRulesetType } from "types/InpatientRuleset";

const ExampleContainer = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 1rem;
  margin-top: 0.5rem;
`;

const FormulaLine = styled.div`
  font-family: monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #495057;
`;

const ResultLine = styled.div`
  font-family: monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #2e7d32;
  font-weight: 600;
`;

const ScenarioLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
`;

const fmt = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const fmtDec = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export interface IExampleFormValues {
  // Fixed Cost
  costToChargeRatio: number | null;
  fixedLossThresholdInCents: number | null;
  outlierPercent: number | null;
  // Pass Through
  thresholdInCents: number | null;
  chargeMultiplier: number | null;
  // Transfer
  losFormula: "LOS" | "LOS_PLUS_1";
  // Stop Loss
  thresholdMultiplier: number | null;
  paymentPercent: number | null;
  // Charge Outlier
  chargeOutlierThresholdType: "FLAT" | "MULTIPLIER";
  chargeOutlierThresholdAmountInCents: number | null;
  chargeOutlierThresholdMultiplier: number | null;
  chargeOutlierPercent: number | null;
  chargeOutlierFloorInCents: number | null;
}

// Sample claim values used in examples (not configurable on the form)
const SAMPLE = {
  drgRateCents: 1500000, // $15,000
  totalChargesCents: 5000000, // $50,000
  serviceLineChargesCents: 1000000, // $10,000
  drgRateLos: 2000000, // $20,000
  alos: 5,
  losThreshold: 8,
  losMultiplier: 0.7,
  actualLos: 10,
  transferLos: 3,
  stopLossDrgCents: 1500000, // $15,000
  stopLossChargesCents: 3500000, // $35,000
  chargeOutlierBaseExpected: 1500000, // $15,000
  chargeOutlierChargesCents: 15000000, // $150,000
};

function FixedCostExample({ values }: { values: IExampleFormValues }) {
  const ccr = values.costToChargeRatio ?? 0.95;
  const thresholdCents = values.fixedLossThresholdInCents ?? 20000;
  const outlierPct = values.outlierPercent ?? 80;
  const drgRate = SAMPLE.drgRateCents;
  const charges = SAMPLE.totalChargesCents;

  const estimatedCost = Math.round(charges * ccr);
  const triggerAmount = drgRate + thresholdCents;
  const outlierApplies = estimatedCost > triggerAmount;
  const outlierPayment = outlierApplies
    ? Math.round((outlierPct / 100) * (estimatedCost - triggerAmount))
    : 0;
  const expected = drgRate + outlierPayment;

  return (
    <ExampleContainer>
      <ScenarioLabel>Example Calculation</ScenarioLabel>
      <FormulaLine>DRG Rate (base expected) = {fmt(drgRate)}</FormulaLine>
      <FormulaLine>Total Charges = {fmt(charges)}</FormulaLine>
      <FormulaLine>
        CCR = {ccr}, Threshold = {fmtDec(thresholdCents)}, Outlier% ={" "}
        {outlierPct}%
      </FormulaLine>

      <ScenarioLabel style={{ marginTop: "0.75rem" }}>Steps</ScenarioLabel>
      <FormulaLine>
        1. Estimated Cost = {fmt(charges)} × {ccr} = {fmt(estimatedCost)}
      </FormulaLine>
      <FormulaLine>
        2. Trigger Amount = {fmt(drgRate)} + {fmtDec(thresholdCents)} ={" "}
        {fmt(triggerAmount)}
      </FormulaLine>
      {outlierApplies ? (
        <>
          <FormulaLine>
            3. {fmt(estimatedCost)} &gt; {fmt(triggerAmount)} → outlier applies
          </FormulaLine>
          <FormulaLine>
            4. Outlier Payment = {outlierPct}% × ({fmt(estimatedCost)} −{" "}
            {fmt(triggerAmount)}) = {fmt(outlierPayment)}
          </FormulaLine>
          <ResultLine>
            Expected = {fmt(drgRate)} + {fmt(outlierPayment)} = {fmt(expected)}
          </ResultLine>
        </>
      ) : (
        <>
          <FormulaLine>
            3. {fmt(estimatedCost)} ≤ {fmt(triggerAmount)} → no outlier
          </FormulaLine>
          <ResultLine>Expected = {fmt(drgRate)} (base DRG rate)</ResultLine>
        </>
      )}

      <div
        className="text-600 mt-2"
        style={{ fontSize: "0.8rem", fontStyle: "italic" }}
      >
        If Estimated Cost is below the Trigger Amount, no outlier applies and
        the expected amount remains at the base DRG rate.
      </div>
    </ExampleContainer>
  );
}

function PassThroughExample({ values }: { values: IExampleFormValues }) {
  const thresholdCents = values.thresholdInCents ?? 0;
  const multiplier = values.chargeMultiplier ?? 1.25;
  const charges = SAMPLE.serviceLineChargesCents;

  const applies = charges > thresholdCents;
  const expected = applies ? Math.round(charges * multiplier) : 0;

  return (
    <ExampleContainer>
      <ScenarioLabel>Example Calculation</ScenarioLabel>
      <FormulaLine>Service line charges = {fmt(charges)}</FormulaLine>
      <FormulaLine>
        Charge Threshold = {fmtDec(thresholdCents)}, Charge Multiplier ={" "}
        {multiplier}
      </FormulaLine>

      <ScenarioLabel style={{ marginTop: "0.75rem" }}>Steps</ScenarioLabel>
      {applies ? (
        <>
          <FormulaLine>
            1. {fmt(charges)} &gt; {fmtDec(thresholdCents)} threshold →
            pass-through applies
          </FormulaLine>
          <FormulaLine>
            2. Expected = {fmt(charges)} × {multiplier} = {fmt(expected)}
          </FormulaLine>
          <ResultLine>
            Pass-through amount added to base expected = {fmt(expected)}
          </ResultLine>
        </>
      ) : (
        <>
          <FormulaLine>
            1. {fmt(charges)} ≤ {fmtDec(thresholdCents)} threshold →
            pass-through does not apply
          </FormulaLine>
          <ResultLine>No pass-through amount added</ResultLine>
        </>
      )}

      <div
        className="text-600 mt-2"
        style={{ fontSize: "0.8rem", fontStyle: "italic" }}
      >
        Pass-through pricing is used for service line codes that have no fee
        schedule match. Each matching code&apos;s charges are multiplied
        individually, then summed.
      </div>
    </ExampleContainer>
  );
}

function ExcessiveLosExample() {
  const drgRate = SAMPLE.drgRateLos;
  const { alos } = SAMPLE;
  const threshold = SAMPLE.losThreshold;
  const multiplier = SAMPLE.losMultiplier;
  const los = SAMPLE.actualLos;

  const perDiem = Math.round(multiplier * (drgRate / alos));
  const outlierDays = los - threshold;
  const addOn = perDiem * outlierDays;
  const expected = drgRate + addOn;

  return (
    <ExampleContainer>
      <ScenarioLabel>Example Calculation</ScenarioLabel>
      <FormulaLine>DRG Base Rate = {fmt(drgRate)}</FormulaLine>
      <FormulaLine>Average LOS (ALOS) = {alos} days</FormulaLine>
      <FormulaLine>
        Outlier Threshold = {threshold} days, Multiplier = {multiplier}
      </FormulaLine>
      <FormulaLine>Actual LOS = {los} days</FormulaLine>

      <ScenarioLabel style={{ marginTop: "0.75rem" }}>Steps</ScenarioLabel>
      <FormulaLine>
        1. {los} days &gt; {threshold} day threshold → day outlier applies
      </FormulaLine>
      <FormulaLine>
        2. Per Diem = {multiplier} × ({fmt(drgRate)} ÷ {alos}) = {fmt(perDiem)}
        /day
      </FormulaLine>
      <FormulaLine>
        3. Outlier Days = {los} − {threshold} = {outlierDays} days
      </FormulaLine>
      <FormulaLine>
        4. Outlier Add-on = {fmt(perDiem)} × {outlierDays} = {fmt(addOn)}
      </FormulaLine>
      <ResultLine>
        Expected = {fmt(drgRate)} + {fmt(addOn)} = {fmt(expected)}
      </ResultLine>

      <div
        className="text-600 mt-2"
        style={{ fontSize: "0.8rem", fontStyle: "italic" }}
      >
        The ALOS, outlier threshold, and multiplier come from the DRG fields on
        the ProviderProcedure record. If LOS does not exceed the threshold, the
        base DRG rate is used with no add-on.
      </div>
    </ExampleContainer>
  );
}

function TransferExample({ values }: { values: IExampleFormValues }) {
  const { losFormula } = values;
  const drgRate = SAMPLE.drgRateLos;
  const { alos } = SAMPLE;
  const los = SAMPLE.transferLos;

  const perDiem = Math.round(drgRate / alos);
  const losDays = losFormula === "LOS_PLUS_1" ? los + 1 : los;
  const transferPayment = perDiem * losDays;
  const expected = Math.min(drgRate, transferPayment);
  const formulaLabel =
    losFormula === "LOS_PLUS_1" ? "Per Diem × (LOS + 1)" : "Per Diem × LOS";
  const losDaysCalc =
    losFormula === "LOS_PLUS_1" ? `${los} + 1 = ${losDays}` : `${los}`;

  return (
    <ExampleContainer>
      <ScenarioLabel>Example Calculation</ScenarioLabel>
      <FormulaLine>DRG Base Rate = {fmt(drgRate)}</FormulaLine>
      <FormulaLine>Average LOS (ALOS) = {alos} days</FormulaLine>
      <FormulaLine>Actual LOS = {los} days</FormulaLine>
      <FormulaLine>LOS Formula = {formulaLabel}</FormulaLine>
      <FormulaLine>Discharge Status = 02 (transferred)</FormulaLine>

      <ScenarioLabel style={{ marginTop: "0.75rem" }}>Steps</ScenarioLabel>
      <FormulaLine>
        1. Discharge status 02 matches configured codes → transfer applies
      </FormulaLine>
      <FormulaLine>
        2. Per Diem = {fmt(drgRate)} ÷ {alos} = {fmt(perDiem)}/day
      </FormulaLine>
      <FormulaLine>
        3. LOS Days = {losDaysCalc} (using{" "}
        {losFormula === "LOS_PLUS_1" ? "LOS + 1" : "LOS"} formula)
      </FormulaLine>
      <FormulaLine>
        4. Transfer Payment = {fmt(perDiem)} × {losDays} ={" "}
        {fmt(transferPayment)}
      </FormulaLine>
      <FormulaLine>
        5. Cap at DRG Rate: min({fmt(drgRate)}, {fmt(transferPayment)}) ={" "}
        {fmt(expected)}
      </FormulaLine>
      <ResultLine>Expected = {fmt(expected)}</ResultLine>

      <div
        className="text-600 mt-2"
        style={{ fontSize: "0.8rem", fontStyle: "italic" }}
      >
        Transfer pricing is capped at the full DRG rate — if Per Diem × days
        exceeds the DRG rate, the DRG rate is used instead. Only claims with a
        matching discharge status code trigger transfer pricing.
      </div>
    </ExampleContainer>
  );
}

function StopLossExample({ values }: { values: IExampleFormValues }) {
  const thresholdMult = values.thresholdMultiplier ?? 200;
  const paymentPct = values.paymentPercent ?? 75;
  const drgRate = SAMPLE.stopLossDrgCents;
  const charges = SAMPLE.stopLossChargesCents;

  const thresholdAmount = Math.round(drgRate * (thresholdMult / 100));
  const applies = charges > thresholdAmount;
  const stopLossPayment = applies
    ? Math.round((paymentPct / 100) * charges)
    : 0;

  return (
    <ExampleContainer>
      <ScenarioLabel>Example Calculation</ScenarioLabel>
      <FormulaLine>DRG Rate (base expected) = {fmt(drgRate)}</FormulaLine>
      <FormulaLine>Total Billed Charges = {fmt(charges)}</FormulaLine>
      <FormulaLine>
        Threshold = {thresholdMult}% of DRG, Payment = {paymentPct}% of charges
      </FormulaLine>

      <ScenarioLabel style={{ marginTop: "0.75rem" }}>Steps</ScenarioLabel>
      <FormulaLine>
        1. Threshold Amount = {fmt(drgRate)} × {thresholdMult}% ={" "}
        {fmt(thresholdAmount)}
      </FormulaLine>
      {applies ? (
        <>
          <FormulaLine>
            2. {fmt(charges)} &gt; {fmt(thresholdAmount)} → stop loss applies
          </FormulaLine>
          <FormulaLine>
            3. Stop Loss Payment = {paymentPct}% × {fmt(charges)} ={" "}
            {fmt(stopLossPayment)}
          </FormulaLine>
          <ResultLine>
            Expected = {fmt(stopLossPayment)} (replaces DRG rate)
          </ResultLine>
        </>
      ) : (
        <>
          <FormulaLine>
            2. {fmt(charges)} ≤ {fmt(thresholdAmount)} → stop loss does not
            apply
          </FormulaLine>
          <ResultLine>Expected = {fmt(drgRate)} (base DRG rate)</ResultLine>
        </>
      )}

      <div
        className="text-600 mt-2"
        style={{ fontSize: "0.8rem", fontStyle: "italic" }}
      >
        Stop loss replaces the base expected amount entirely (it is not added to
        the DRG rate). If charges do not exceed the threshold, stop loss does
        not apply and the DRG rate is used.
      </div>
    </ExampleContainer>
  );
}

function ChargeOutlierExample({ values }: { values: IExampleFormValues }) {
  const thresholdType = values.chargeOutlierThresholdType ?? "FLAT";
  const outlierPct = values.chargeOutlierPercent ?? 30;
  const baseExpected = SAMPLE.chargeOutlierBaseExpected;
  const charges = SAMPLE.chargeOutlierChargesCents;
  const floorCents = values.chargeOutlierFloorInCents;

  let threshold: number;
  let thresholdLabel: string;
  if (thresholdType === "FLAT") {
    const flatAmount = values.chargeOutlierThresholdAmountInCents ?? 12500000;
    threshold = flatAmount;
    thresholdLabel = `Flat threshold = ${fmtDec(flatAmount)}`;
  } else {
    const multiplier = values.chargeOutlierThresholdMultiplier ?? 3.0;
    threshold = Math.round(multiplier * baseExpected);
    thresholdLabel = `Threshold = ${multiplier} \u00d7 ${fmt(
      baseExpected
    )} = ${fmt(threshold)}`;
  }

  const meetsFloor = !floorCents || charges > floorCents;
  const applies = meetsFloor && charges > threshold;
  const excessCharges = applies ? charges - threshold : 0;
  const outlierPayment = applies
    ? Math.round((outlierPct / 100) * excessCharges)
    : 0;
  const expected = baseExpected + outlierPayment;

  return (
    <ExampleContainer>
      <ScenarioLabel>Example Calculation</ScenarioLabel>
      <FormulaLine>
        Base Expected (DRG/Per Diem) = {fmt(baseExpected)}
      </FormulaLine>
      <FormulaLine>Total Billed Charges = {fmt(charges)}</FormulaLine>
      <FormulaLine>
        {thresholdLabel}, Outlier% = {outlierPct}%
        {floorCents ? `, Charge Floor = ${fmtDec(floorCents)}` : ""}
      </FormulaLine>

      <ScenarioLabel style={{ marginTop: "0.75rem" }}>Steps</ScenarioLabel>
      {floorCents && (
        <FormulaLine>
          1. Charge floor check: {fmt(charges)} {meetsFloor ? ">" : "\u2264"}{" "}
          {fmtDec(floorCents)}{" "}
          {meetsFloor ? "\u2192 passes" : "\u2192 fails, no outlier"}
        </FormulaLine>
      )}
      <FormulaLine>
        {floorCents ? "2" : "1"}. Threshold: {thresholdLabel}
      </FormulaLine>
      {applies ? (
        <>
          <FormulaLine>
            {floorCents ? "3" : "2"}. {fmt(charges)} &gt; {fmt(threshold)}{" "}
            &rarr; outlier applies
          </FormulaLine>
          <FormulaLine>
            {floorCents ? "4" : "3"}. Excess = {fmt(charges)} &minus;{" "}
            {fmt(threshold)} = {fmt(excessCharges)}
          </FormulaLine>
          <FormulaLine>
            {floorCents ? "5" : "4"}. Outlier Payment = {outlierPct}% &times;{" "}
            {fmt(excessCharges)} = {fmt(outlierPayment)}
          </FormulaLine>
          <ResultLine>
            Expected = {fmt(baseExpected)} + {fmt(outlierPayment)} ={" "}
            {fmt(expected)}
          </ResultLine>
        </>
      ) : (
        <>
          <FormulaLine>
            {floorCents ? "3" : "2"}. {fmt(charges)} &le; {fmt(threshold)}{" "}
            &rarr; no outlier
          </FormulaLine>
          <ResultLine>Expected = {fmt(baseExpected)} (base only)</ResultLine>
        </>
      )}

      <div
        className="text-600 mt-2"
        style={{ fontSize: "0.8rem", fontStyle: "italic" }}
      >
        The outlier payment is added to the base expected amount. The threshold
        is computed from{" "}
        {thresholdType === "FLAT"
          ? "a fixed dollar amount"
          : "a multiplier of the base expected (DRG rate or per diem \u00d7 LOS)"}
        .
        {floorCents
          ? " Both the charge floor and threshold must be exceeded for the outlier to apply."
          : ""}
      </div>
    </ExampleContainer>
  );
}

export interface IInpatientCalculationExampleProps {
  type: InpatientRulesetType;
  values: IExampleFormValues;
}

export function InpatientCalculationExample({
  type,
  values,
}: IInpatientCalculationExampleProps) {
  switch (type) {
    case "FIXED_COST":
      return (
        <div className="mt-3">
          <FixedCostExample values={values} />
        </div>
      );
    case "PASS_THROUGH":
      return (
        <div className="mt-3">
          <PassThroughExample values={values} />
        </div>
      );
    case "EXCESSIVE_LOS":
      return (
        <div className="mt-3">
          <ExcessiveLosExample />
        </div>
      );
    case "TRANSFER":
      return (
        <div className="mt-3">
          <TransferExample values={values} />
        </div>
      );
    case "STOP_LOSS":
      return (
        <div className="mt-3">
          <StopLossExample values={values} />
        </div>
      );
    case "CHARGE_OUTLIER":
      return (
        <div className="mt-3">
          <ChargeOutlierExample values={values} />
        </div>
      );
    default:
      return null;
  }
}
